import { useState } from 'react';
import { X, Trash2, Upload, RotateCcw, Clock, Pencil, Check, Share2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useCreateShareLink } from '@/hooks/useSandboxDrafts';
import { useUIStore } from '@/stores/ui-store';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cn } from '@/lib/utils';
import {
  useSandboxDrafts,
  useDeleteDraft,
  useFetchDraftConfig,
  useUpdateDraft,
  type SandboxDraft,
} from '@/hooks/useSandboxDrafts';
import type { DesignThemeConfig } from '@/stores/design-preview-store';

const SOURCE_LABEL: Record<SandboxDraft['source'], string> = {
  manual: 'Custom',
  gallery: 'Template',
  ai: 'AI',
  backup: 'Backup',
};

/**
 * Slide-over listing saved sandbox drafts. "Load" pulls a draft's full config
 * into the candidate for previewing; "Revert" (backup drafts only) re-publishes
 * a pre-publish snapshot to the live site. Delete removes a draft.
 */
export default function SandboxDraftsPanel({
  onClose,
  onLoad,
  onRevert,
}: {
  onClose: () => void;
  onLoad: (config: DesignThemeConfig) => void;
  onRevert: (config: DesignThemeConfig, draft: SandboxDraft) => void;
}) {
  const { data: drafts, isLoading } = useSandboxDrafts();
  const fetchConfig = useFetchDraftConfig();
  const deleteDraft = useDeleteDraft();
  const updateDraft = useUpdateDraft();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const shareLink = useCreateShareLink();
  const addToast = useUIStore((st) => st.addToast);
  const [sharingId, setSharingId] = useState<string | null>(null);

  /**
   * Mint a share link and put it straight on the clipboard.
   *
   * Copying rather than showing the URL is deliberate: the next thing anyone
   * does with it is paste it into a message, and a link displayed in a panel
   * has to be selected out of the DOM by hand. The toast carries the expiry,
   * because "7 days" is the part people forget and then wonder why the client
   * says the link is dead.
   */
  const shareDraft = async (id: string) => {
    setSharingId(id);
    try {
      const link = await shareLink.mutateAsync({ draftId: id });
      try {
        await navigator.clipboard.writeText(link.url);
        addToast('Preview link copied — it works for 7 days, for anyone you send it to', 'success');
      } catch {
        // Clipboard can be blocked (insecure context, permission). The link
        // still exists, so say so rather than implying the whole thing failed.
        addToast(`Preview link created: ${link.url}`, 'success');
      }
    } catch {
      addToast('Could not create a preview link', 'error');
    } finally {
      setSharingId(null);
    }
  };

  const withConfig = async (id: string, fn: (cfg: DesignThemeConfig, d: SandboxDraft) => void) => {
    setBusyId(id);
    try {
      const draft = await fetchConfig.mutateAsync(id);
      if (draft.config) fn(draft.config, draft);
    } finally {
      setBusyId(null);
    }
  };

  const startRename = (d: SandboxDraft) => {
    updateDraft.reset(); // clear any stale error from a prior failed rename
    setEditingId(d.id);
    setEditName(d.name);
  };

  const saveRename = async (d: SandboxDraft) => {
    const name = editName.trim();
    if (!name || name === d.name) {
      setEditingId(null);
      return;
    }
    try {
      await updateDraft.mutateAsync({ id: d.id, name });
      setEditingId(null); // close only on success
    } catch {
      // Keep the editor open so the typed name isn't lost; error shown inline.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Saved designs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Saved designs</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" aria-label="Close saved designs">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {isLoading && (
            <div className="flex justify-center py-8" role="status">
              <LoadingSpinner />
              <span className="sr-only">Loading saved designs…</span>
            </div>
          )}
          {!isLoading && (!drafts || drafts.length === 0) && (
            <p className="text-sm text-gray-500 text-center py-8">
              No saved designs yet. Pick or customize a design, then “Save draft”.
            </p>
          )}
          {drafts?.map((d) => {
            const isBackup = d.source === 'backup';
            const rowBusy = busyId === d.id;
            return (
              <div key={d.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  {editingId === d.id ? (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(d);
                            else if (e.key === 'Escape') setEditingId(null);
                          }}
                          autoFocus
                          aria-label={`Rename ${d.name}`}
                          className="flex-1 min-w-0 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <button
                          onClick={() => saveRename(d)}
                          disabled={updateDraft.isPending}
                          className="p-1.5 rounded-md hover:bg-green-50 text-gray-400 hover:text-green-600 flex-shrink-0"
                          aria-label="Save name"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 flex-shrink-0"
                          aria-label="Cancel rename"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {updateDraft.isError && (
                        <p className="text-xs text-red-600 mt-1" role="alert">
                          Couldn’t rename — please try again.
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">{d.name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className={cn(
                              'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
                              isBackup ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {SOURCE_LABEL[d.source]}
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            {new Date(d.updated_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => startRename(d)}
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                          aria-label={`Rename ${d.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteDraft.mutate(d.id)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-600"
                          aria-label={`Delete ${d.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => withConfig(d.id, (cfg) => onLoad(cfg))} isLoading={rowBusy}>
                    <Upload className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    Load
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => shareDraft(d.id)}
                    isLoading={shareLink.isPending && sharingId === d.id}
                    title="Copy a link that shows this design on the real storefront to anyone you send it to — no login needed. Expires in 7 days and can be turned off."
                  >
                    <Share2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    Share link
                  </Button>
                  {isBackup && (
                    <Button size="sm" variant="ghost" onClick={() => withConfig(d.id, (cfg, draft) => onRevert(cfg, draft))} isLoading={rowBusy}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                      Revert live to this
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
